import React from 'react';
import classNames from 'classnames';

import Conditional from '../../../common/conditional';
import CustomReviewDetails from '../custom-review-details';
import CustomSectionHeader from '../../custom-section-header';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import { concatClassNames, getPrefixedModule } from '../../../../utils/utilities';
import { Column, CoverDataOverview, CoverDataItem, Project } from '../../../../utils/models';
import { SHELL_ID } from '../../../../utils/ids';

import styles from './style.module.scss';
import useInspectorDetails from '../../../../hooks/useInspectorDetails';

interface OverviewSheetProps {
	projectDetails: Project;
	overviewData: Omit<CoverDataOverview, 'required'>[];
	referenceDocuments: CoverDataItem[];
	standardCriteriaItems: CoverDataItem[];
}

const OverviewSheet: React.FC<OverviewSheetProps> = (props) => {
	const {
		overviewData,
		referenceDocuments,
		standardCriteriaItems,
		projectDetails: {
			equipmentTag,
			equipmentDescription,
			module,
			scope,
			organisation,
			facility,
		},
	} = props;

	const { 
		inspectorQualifications, 
		inspectorName,
		inspectorSignature,
		inspectionDate, 
		reviewerInfo, 
		approverInfo,
	} = useInspectorDetails(props.projectDetails);

	const contentMargin = overviewData.length > 10 ? "mt-1" : "mt-4";

	return (
		<div className="pdf-page p-3" aria-label="Cover Sheet">
			<CustomSectionHeader
				organizationLogo={organisation.organisationLogo} 
				organizationName={organisation.organisationName}
				facilityLogo={organisation._id === SHELL_ID ? '' : facility.facilityLogo}
				facilityName={facility.facilityName}
			/> 

			<div className={styles.overviewSheet}>
				<div className="container-fluid overflow-hidden p-0">
					<div className="row">
						<div className={concatClassNames([styles.overviewSheetGridItem,'col'])}>
							<span>Scope: </span> {scope}
						</div>

						<div className={concatClassNames([styles.overviewSheetGridItem, styles.overviewSheetColored, 'col', 'border-dark', 'border-top-0', 'border-bottom-0'])}>
							<span className={styles.overviewSheetGridItemTitle}>
								COVER SHEET
							</span>
						</div>

						<div className={concatClassNames([styles.overviewSheetGridItem, 'col'])}>
							<span>Description: </span> {equipmentDescription}
						</div>
					</div>

					<div className={concatClassNames([styles.overviewSheetGridItemSub, styles.overviewSheetColored])}>
						<span className={concatClassNames([styles.overviewSheetGridItemTitle, styles.overviewSheetGridItemTitleHeader])}>
							{getPrefixedModule(module)} - {equipmentTag}
						</span>
					</div>
				</div>

				<div className={classNames(["border-dark m-1", contentMargin])}>
					<PDFTablePage columns={overviewColumns} data={overviewData} />
				</div>

				<Conditional isVisible={referenceDocuments.length > 0}>
					<div className={classNames(["m-1", contentMargin])}>
						<div className={styles.gridTitle}>
							Reference Documents/Work Instruction
						</div>

						<div className="border-dark container-fluid overflow-hidden">
							{referenceDocuments.map((item: CoverDataItem) => (
								<div className="row" key={item.label}>
									<div className={concatClassNames([styles.grid, 'col'])}>
										{item.label}
									</div>
									<div className={concatClassNames([styles.grid, 'col'])}>
										{item.value}
									</div>
								</div>
							))}
						</div>
					</div>
				</Conditional>

				<Conditional isVisible={standardCriteriaItems.length > 0}>
					<div className="m-1">
						<div className={styles.gridTitle}>
							Inspection Standard / Criteria
						</div>

						<div className="border-dark container-fluid overflow-hidden">
							{standardCriteriaItems.map((item: CoverDataItem) => (
								<div className="row" key={item.label}>
									<div className={concatClassNames([styles.grid, 'col'])}>
										{item.label}
									</div>
									<div className={concatClassNames([styles.grid, 'col'])}>
										{item.value}
									</div>
								</div>
							))}
						</div>
					</div>
				</Conditional>

				<div className={classNames(["p-1", contentMargin])}>
					<CustomReviewDetails
						inspectorName={inspectorName}
						inspectorQualifications={inspectorQualifications}
						inspectorSignature={inspectorSignature}
						inspectionDate={inspectionDate}
						reviewerName={reviewerInfo.reviewerName}
						reviewDate={reviewerInfo.reviewDate}
						reviewerQualifications={reviewerInfo.reviewerQualifications}
						reviewerSignature={reviewerInfo.reviewerSignature}
						approverName={approverInfo.approverName}
						approvalDate={approverInfo.approvalDate}
						approverQualifications={approverInfo.approverQualifications}
						approverSignature={approverInfo.approverSignature}
						inspectorRole=""
						reviewerRole=""
						approverRole=""
					/>
				</div>
			</div>
		</div>
	);
};

const overviewColumns: Column[] = [
	{
		path: 'report',
		label: 'Description',
		style: () => ({ textAlign: 'left' }),
	},
	{ path: 'page', label: 'Page Number' },
];

export default OverviewSheet;
